"use client";

import React, { useState, useRef, useEffect } from "react";
import { PageGeneralRulesSchema } from "@/sanity/pages/PageGeneralRulesTemplate";
import { PortableText } from "next-sanity";
import type { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Footer from "@/components/global/partials/Footer";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Image from "next/image";
import { GROQImageLoader } from "@/sanity/lib/definitions";
import { cn } from "@/lib/utils";

import CornerSvg from "@/components/global/ui/CornerSvg";
import InvertedCornerSvg from "../global/ui/InvertedCornerSvg";
import LastCTASection from "../global/sections/LastCTA";

/* ─── Theme helpers ─────────────────────────────────────────────── */

const themeColors = {
  crypto: {
    bg: "bg-[#E5ECE8]",
    bgHalf: "bg-[#E5ECE8]/50",
    fill: "#E5ECE8",
    accent: "bg-[#95B0A1]",
    accentLight: "bg-[#95B0A1]/[15%]",
    accentBorder: "border-black/20",
    accentDivider: "bg-[#95B0A1]/30",
    accentBg20: "bg-[#95B0A1]/20",
    tabText: "!text-[#103037]",
    tabHover: "hover:bg-[#d4e8dd]",
  },
  future: {
    bg: "bg-[#E5E5EC]",
    bgHalf: "bg-[#E5E5EC]/50",
    fill: "#E5E5EC",
    accent: "bg-[#4B4266]",
    accentLight: "bg-[#9C95B0]/[15%]",
    accentBorder: "border-[#9C95B0]/30",
    accentDivider: "bg-[#9C95B0]/30",
    accentBg20: "bg-[#9C95B0]/20",
    tabText: "!text-[#4B4266]",
    tabHover: "hover:bg-[#4B4266]/20",
  },
} as const;

type Category = "crypto" | "future";

/* ─── Reusable sub-components ───────────────────────────────────── */

function ThemeDivider({ category }: { category: Category }) {
  return (
    <div
      className={cn("w-full h-[.125rem]", themeColors[category].accentDivider)}
    />
  );
}

function RichTextBlock({
  value,
  className,
}: {
  value: PortableTextBlock[];
  className: string;
}) {
  return (
    <div className={cn("richtext richtext-start", className)}>
      <PortableText value={value} components={richTextComponent} />
    </div>
  );
}

interface ComparisonBlockData {
  title?: PortableTextBlock[];
  text?: PortableTextBlock[];
}

function ComparisonCard({
  data,
  className,
  titleClassName,
  textClassName,
}: {
  data: ComparisonBlockData;
  className: string;
  titleClassName: string;
  textClassName: string;
}) {
  return (
    <div className={className}>
      {data.title && (
        <RichTextBlock
          value={data.title}
          className={cn("mb-[1rem]", titleClassName)}
        />
      )}
      {data.text && (
        <RichTextBlock value={data.text} className={textClassName} />
      )}
    </div>
  );
}

function ComparisonPair({
  comp1,
  comp2,
  gridClassName,
  cardClassName,
  card2ClassName,
  titleClassName,
  textClassName,
}: {
  comp1?: ComparisonBlockData;
  comp2?: ComparisonBlockData;
  gridClassName: string;
  cardClassName: string;
  card2ClassName?: string;
  titleClassName: string;
  textClassName: string;
}) {
  return (
    <div className={gridClassName}>
      {comp1 && (
        <ComparisonCard
          data={comp1}
          className={cardClassName}
          titleClassName={titleClassName}
          textClassName={textClassName}
        />
      )}
      {comp2 && (
        <ComparisonCard
          data={comp2}
          className={card2ClassName ?? cardClassName}
          titleClassName={titleClassName}
          textClassName={textClassName}
        />
      )}
    </div>
  );
}

function SectionRow({
  label,
  hasAdditionalItems,
  children,
}: {
  label: string;
  hasAdditionalItems: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col lg:flex-row gap-[1rem]">
      <div className={hasAdditionalItems ? "w-[16rem] shrink-0" : "shrink-0"}>
        <h4 className="uppercase font-[600] text-[2rem] text-[#1C1F22] font-geist shrink-0 lg:font-[500]">
          {label}
        </h4>
      </div>
      {children}
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────── */

export default function PageGeneralRulesJSX({
  data,
}: {
  data: PageGeneralRulesSchema;
}) {
  const [activeRulesCategory, setActiveRulesCategory] =
    useState<Category>("crypto");
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [activeFaqSectionIndex, setActiveFaqSectionIndex] = useState(0);
  const faqSectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const currentItems =
    activeRulesCategory === "crypto" ? data?.cryptoItems : data?.futureItems;
  const activeItem = currentItems?.[activeSubTab];

  const theme = themeColors[activeRulesCategory];

  const activeFaqGroup = data?.faq?.find(
    (group) => group.category === activeRulesCategory,
  );

  const faqItems = activeFaqGroup?.faqItems;
  const faqItemsLength = faqItems?.length ?? 0;

  // Reset active FAQ section when category changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setActiveFaqSectionIndex(0);
    faqSectionRefs.current = [];
  }, [faqItemsLength]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Small delay to ensure refs are populated after conditional render
    const timer = setTimeout(() => {
      const sections: HTMLElement[] = [];
      for (let i = 0; i < faqItemsLength; i++) {
        const el = document.getElementById(`faq-rules-section-${i}`);
        if (el) sections.push(el);
      }

      if (sections.length === 0) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target.id;
              const match = id.match(/faq-rules-section-(\d+)/);
              if (match) {
                setActiveFaqSectionIndex(Number(match[1]));
              }
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "-10% 0px -60% 0px",
        },
      );

      sections.forEach((el) => {
        observer.observe(el);
      });

      observerRef.current = observer;
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [faqItemsLength]);

  const scrollToFaqSection = (index: number) => {
    const section = document.getElementById(`faq-rules-section-${index}`);
    if (section) {
      const y = section.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <main className="bg-white pt-[2rem] lg:pt-[5rem]">
      {data?.settings?.header && (
        <NavBarV2 headerData={data.settings.header} animate={true} />
      )}

      {/* Hero Section */}
      <section className="relative px-[2rem] pt-[12rem] lg:px-[5rem] lg:pt-[18rem]">
        <div className="w-[min(146.625rem,100%)] mx-auto flex relative z-10 flex-col ">
          {data?.heroTitle && (
            <RichTextBlock
              value={data.heroTitle}
              className="prose-h3:text-[#103037] lg:prose-h3:!text-[7rem] prose-h3:!text-[5rem] prose-h3:font-[600] prose-h1:text-[5rem] prose-h3:tracking-[-1.68px] prose-p:text-[#1C1F22]/80 prose-p:text-[2rem] prose-p:leading-[150%] prose-strong:font-[400] prose-strong:text-[#1C1F22] w-[min(95.25rem,100%)]"
            />
          )}
        </div>
      </section>

      {/* Rules Section */}
      <section className="w-[min(146.625rem,100%)] mx-auto px-[2rem] lg:px-0">
        {/* Category Tabs */}
        <div className="flex relative translate-y-1/2 z-[2] gap-[2rem]">
          {(["crypto", "future"] as const).map((category) => {
            const hasItems =
              category === "crypto"
                ? data?.cryptoItems && data.cryptoItems.length > 0
                : data?.futureItems && data.futureItems.length > 0;
            if (!hasItems) return null;

            const isActive = activeRulesCategory === category;
            const catTheme = themeColors[category];
            const isCrypto = activeRulesCategory === "crypto";
            return (
              <button
                type="button"
                key={category}
                onClick={() => {
                  setActiveRulesCategory(category);
                  setActiveSubTab(0);
                  setExpandedFaq(null);
                }}
                className={cn(
                  "h-[12.5rem] lg:w-[25rem] lg:rounded-t-[3rem] cursor-pointer transition-all z-[5] relative select-none w-[16.5rem] rounded-t-[2rem]",
                  isActive
                    ? cn(catTheme.bg, "z-[5]")
                    : cn(catTheme.bgHalf, "z-[2]"),
                )}
              >
                <p
                  className={cn(
                    "m-0 text-[#103037] font-instrument-sans lg:text-[4rem] text-[3rem] font-[500] absolute top-[10%] left-1/2 -translate-x-1/2 tracking-[-.96px] leading-[100%] transition-opacity",
                    isActive ? "opacity-100" : "opacity-40",
                  )}
                >
                  {category === "crypto" ? "Crypto" : "Futures"}
                </p>

                <CornerSvg
                  fill={isCrypto ? "#E5ECE8" : "#E5E5EC"}
                  className={`absolute scale-75 w-[4rem] h-[4rem] top-0 right-0 translate-x-[88%] translate-y-[70%] transition-all duration-300  ${isActive ? "!opacity-100" : "opacity-50"}`}
                />

                {category !== "crypto" && (
                  <InvertedCornerSvg
                    fill={"#E5E5EC"}
                    className={`absolute scale-75 top-0 w-[4rem] h-[4rem] left-0 -translate-x-[87%] translate-y-[70%] transition-all duration-300 ${isActive ? "!opacity-100" : "opacity-50"}`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Rules Content */}
        {currentItems && currentItems.length > 0 && (
          <div className="relative z-[6]">
            {activeItem?.rules && (
              <div
                className={cn(
                  "flex relative flex-col mx-auto w-full gap-[3rem] lg:px-[4rem] px-[2rem] py-[4rem] lg:rounded-[5.25rem] z-[4] lg:gap-[1rem] rounded-[2rem]",
                  theme.bg,
                )}
              >
                {currentItems.length > 1 && (
                  <div className="grid grid-cols-2 w-full lg:flex lg:flex-wrap gap-[1rem] mb-[2rem] lg:gap-[2rem]">
                    {currentItems.map((item, index) => (
                      <button
                        type="button"
                        key={item.title}
                        onClick={() => {
                          setActiveSubTab(index);
                        }}
                        className={cn(
                          `px-[2.5rem] py-[1.25rem] rounded-[1rem] text-[1.75rem] lg:text-[2rem] font-medium transition-all cursor-pointer ${index !== 0 && index !== 1 && "col-span-2"}`,
                          activeSubTab === index
                            ? cn(theme.accent, "text-white")
                            : cn(
                                theme.accentLight,
                                theme.tabText,
                                theme.tabHover,
                              ),
                        )}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                )}
                {activeItem.rules.map((rule) => (
                  <div
                    key={rule.title}
                    className={cn(
                      "border overflow-clip rounded-[2rem] lg:p-[3rem] gap-[2rem] lg:rounded-[3rem] border-black/20",
                      theme.accentBorder,
                    )}
                  >
                    {/* Rule Header */}
                    <div className="flex items-center border-b lg:p-0 lg:mb-0 lg:border-b-0 gap-[2.25rem] border-black/20 p-[2rem] mb-[1.5rem]">
                      {rule.icon?.src && (
                        <div className="hidden relative lg:block shrink-0 w-[4rem] h-[4rem] lg:w-[5rem] lg:h-[5rem]">
                          <Image
                            src={rule.icon.src}
                            alt={rule.title || "Rule icon"}
                            fill
                            className="object-contain"
                            loader={GROQImageLoader}
                          />
                        </div>
                      )}
                      <span className="text-[#1C1F22] text-[2.5rem] font-[500]">
                        {rule.title}
                      </span>
                    </div>

                    {/* Rule Content */}
                    <div className="flex flex-col lg:p-0 gap-[2.5rem] pt-[1rem] p-[2rem] lg:pl-[7rem]">
                      {/* Why Section */}
                      {rule.why && (
                        <SectionRow
                          label="Why?"
                          hasAdditionalItems={!!rule.additionalItems}
                        >
                          <RichTextBlock
                            value={rule.why}
                            className="prose-p:text-[#1C1F22]/80 prose-p:m-0 prose-p:text-[2rem]"
                          />
                        </SectionRow>
                      )}

                      {rule?.additionalItems && (
                        <ThemeDivider category={activeRulesCategory} />
                      )}

                      {/* Who / How Section */}
                      {rule?.whoText && (
                        <SectionRow
                          label="HOW?"
                          hasAdditionalItems={!!rule.additionalItems}
                        >
                          {rule.isComparison ? (
                            <ComparisonPair
                              comp1={rule.comp1}
                              comp2={rule.comp2}
                              gridClassName="grid grid-cols-1 w-full lg:grid-cols-2 gap-[1rem]"
                              cardClassName="border rounded-[1.5rem] border-black/10 p-[2.5rem] lg:p-[3rem]"
                              card2ClassName={cn(
                                "border rounded-[1.5rem] p-[2.5rem] lg:p-[3rem]",
                                theme.accentBg20,
                              )}
                              titleClassName="prose-p:m-0 prose-p:text-[2rem] prose-p:text-black prose-strong:font-[500]"
                              textClassName="prose-p:text-black/70 prose-p:text-[2rem] prose-strong:text-black prose-strong:font-[500] prose-p:m-0"
                            />
                          ) : (
                            rule.whoText && (
                              <RichTextBlock
                                value={rule.whoText}
                                className="prose-p:text-[1.75rem] prose-p:text-[#1C1F22]/80 lg:prose-p:text-[2rem]"
                              />
                            )
                          )}
                        </SectionRow>
                      )}

                      {rule?.additionalItems && rule?.whoText && (
                        <ThemeDivider category={activeRulesCategory} />
                      )}

                      {/* Additional Items */}
                      {rule.additionalItems &&
                        rule.additionalItems.length > 0 && (
                          <div className="flex flex-col gap-[2.5rem]">
                            {rule.additionalItems.map((item, itemIndex) => (
                              <React.Fragment key={item.title}>
                                <div className="flex flex-col lg:flex-row gap-[1rem]">
                                  <div className="w-[16rem] shrink-0">
                                    <h4 className="uppercase font-[500] text-[2rem] text-[#1C1F22] font-geist shrink-0 mb-[1rem]">
                                      {item.title}
                                    </h4>
                                  </div>

                                  {item.hasComparison ? (
                                    <ComparisonPair
                                      comp1={item.comp1}
                                      comp2={item.comp2}
                                      gridClassName="grid grid-cols-1 lg:grid-cols-2 gap-[2rem] lg:gap-[3rem]"
                                      cardClassName="border rounded-[1.5rem] border-black/10 p-[2.5rem] lg:p-[3rem]"
                                      titleClassName="prose-p:font-semibold prose-p:text-[1.75rem] lg:prose-p:text-[2rem]"
                                      textClassName="prose-p:text-[1.75rem] prose-p:text-[#1C1F22]/80 lg:prose-p:text-[2rem]"
                                    />
                                  ) : (
                                    item.text && (
                                      <RichTextBlock
                                        value={item.text}
                                        className="prose-p:text-[1.75rem] prose-p:text-[#1C1F22]/80 prose-p:m-0 lg:prose-p:text-[2rem]"
                                      />
                                    )
                                  )}
                                </div>

                                {itemIndex <
                                  (rule.additionalItems?.length ?? 0) - 1 && (
                                  <ThemeDivider
                                    category={activeRulesCategory}
                                  />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        )}

                      {/* Special Additional Items */}
                      {rule.specialAdditionalItems &&
                        rule.specialAdditionalItems.length > 0 && (
                          <div className="flex flex-col gap-[2.5rem]">
                            {rule.specialAdditionalItems.map(
                              (item, itemIndex) => (
                                <React.Fragment key={item.title}>
                                  <div className="flex flex-col gap-[1rem]">
                                    <h4 className="uppercase font-[500] text-[2.5rem] text-[#1C1F22] font-geist shrink-0 mb-[1rem]">
                                      {item.title}
                                    </h4>

                                    <RichTextBlock
                                      value={item.text}
                                      className="prose-p:text-[1.75rem] prose-p:text-[#1C1F22]/80 prose-p:m-0 prose-strong:font-[500] prose-strong:text-black lg:prose-p:text-[2rem]"
                                    />

                                    <ComparisonPair
                                      comp1={item.comp1}
                                      comp2={item.comp2}
                                      gridClassName="grid grid-cols-1 lg:grid-cols-2 gap-[2rem] lg:gap-[3rem]"
                                      cardClassName="rounded-[1.5rem] p-[2.5rem] bg-[#95B0A1]/20 lg:p-[3rem]"
                                      titleClassName="prose-p:font-semibold prose-p:text-[1.75rem] prose-p:text-[#1C1F22] lg:prose-p:text-[2rem]"
                                      textClassName="prose-p:text-[1.75rem] prose-p:text-[#1C1F22]/80 prose-strong:text-[#1C1F22]/80 prose-strong:font-[500] lg:prose-p:text-[2rem]"
                                    />
                                  </div>

                                  {itemIndex <
                                    (rule.additionalItems?.length ?? 0) - 1 && (
                                    <ThemeDivider
                                      category={activeRulesCategory}
                                    />
                                  )}
                                </React.Fragment>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* FAQ Section */}
      {data?.faq && data.faq.length > 0 && (
        <section className="container pt-[8rem] pb-[8rem] lg:pt-[12rem] lg:pb-[16rem]">
          {/* FAQ Items */}

          {/* 146.625rem */}
          <div className="w-[min(146.625rem,100%)] flex !justify-between relative mx-auto lg:flex-row flex-col">
            <div className="flex flex-col w-full lg:hidden mb-[3rem]">
              <p className="uppercase text-[#103037] text-[1.5rem] font-[500] tracking-[2.04px] font-instrument-sans mb-[1.5rem]">
                navigation
              </p>
              {activeFaqGroup?.faqItems && (
                <select
                  className="w-full tracking-tight bg-white border appearance-none cursor-pointer focus:ring-1 focus:outline-none px-[2rem] py-[2.25rem] min-h-[6.75rem] border-[#E5E5E5] rounded-[1rem] font-geist text-[2.25rem] text-[#103037] !font-[600] focus:ring-[#103037]/20"
                  value={activeFaqSectionIndex}
                  onChange={(e) => scrollToFaqSection(Number(e.target.value))}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.368591 0.33786L5.86859 6.33786L11.3686 0.33786' stroke='%23103037'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1.5rem center",
                    backgroundSize: "1.625rem",
                    paddingRight: "2.125rem",
                  }}
                >
                  {activeFaqGroup.faqItems.map((faq, index) => (
                    <option key={faq.title} value={index}>
                      {faq.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {/* 34.75rem */}
            <div className="w-[min(35rem,100%)] shrink-0 hidden lg:block">
              <div className="!h-fit lg:sticky top-[30rem]">
                <p className="uppercase text-[#103037] text-[1.5rem] font-[500] tracking-[2.04px] font-instrument-sans mb-[3.75rem]">
                  navigation
                </p>
                <div className="flex flex-col items-start gap-[2.5rem]">
                  {activeFaqGroup?.faqItems.map((faq, index) => (
                    <button
                      type="button"
                      key={faq.title}
                      onClick={() => scrollToFaqSection(index)}
                      className={cn(
                        "mb-0 leading-none text-[#103037] cursor-pointer transition-opacity duration-300 font-geist text-[2.25rem] tracking-[-.54px] text-left bg-transparent border-none p-0",
                        activeFaqSectionIndex === index
                          ? "opacity-100 font-medium"
                          : "opacity-60 hover:opacity-80 font-normal",
                      )}
                    >
                      {faq.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* 94.25rem */}
            {activeFaqGroup?.faqItems && (
              <div className="flex flex-col">
                {activeFaqGroup.faqItems.map((faqItem, faqItemIndex) => (
                  <div
                    key={faqItem.title}
                    id={`faq-rules-section-${faqItemIndex}`}
                    ref={(el) => {
                      faqSectionRefs.current[faqItemIndex] = el;
                    }}
                    className="relative flex flex-col items-end lg:border-l lg:border-black/20 w-[min(94.25rem,100%)] shrink-0"
                  >
                    <div className="lg:pl-[11.25rem]">
                      <h3 className="font-semibold text-[3rem] text-[#1C1F22] mb-[3rem] lg:text-[4rem] lg:mb-[4rem]">
                        {faqItem.title}
                      </h3>
                      <div className="flex flex-col transition-all duration-300">
                        {faqItem.questions?.map((q, qIndex) => {
                          const faqKey = `${faqItem.title}-${q.question}`;
                          const isOpen = expandedFaq === faqKey;
                          const nextQuestion = faqItem.questions?.[qIndex + 1];
                          const isNextOpen = nextQuestion
                            ? expandedFaq ===
                              `${faqItem.title}-${nextQuestion.question}`
                            : false;

                          return (
                            <React.Fragment key={faqKey}>
                              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                              <div
                                className={`flex cursor-pointer transition-all duration-300 p-[4rem] rounded-[3.5rem] gap-[2.5rem] ${isOpen ? "bg-[#F6F9F7]" : ""}`}
                                onClick={() =>
                                  setExpandedFaq(isOpen ? null : faqKey)
                                }
                              >
                                <div className="relative w-[3.5rem] h-[3.5rem] bg-[#103037] shrink-0 rounded-[1rem]">
                                  <div
                                    className={`absolute transition-all duration-300 top-1/2 left-1/2 !bg-white -translate-x-1/2 -translate-y-1/2 w-[1.625rem] h-[.25rem] ${isOpen ? "rotate-180" : "rotate-90"}`}
                                  />
                                  <div
                                    className={`absolute transition-all duration-300 top-1/2 left-1/2 !bg-white -translate-x-1/2 -translate-y-1/2 w-[1.625rem] h-[.25rem] ${isOpen ? "rotate-180" : "rotate-0"}`}
                                  />
                                </div>
                                <div
                                  className={`flex transition-all duration-200 flex-col w-full ${isOpen ? "gap-[1.5rem]" : "gap-0"}`}
                                >
                                  <span className="text-left select-none text-[#1C1F22] font-[600] text-[2.25rem]">
                                    {q.question}
                                  </span>
                                  <div
                                    className={cn(
                                      "overflow-hidden transition-all duration-200",
                                      isOpen
                                        ? "max-h-[200rem] opacity-100 pb-[2.5rem]"
                                        : "max-h-0 opacity-0",
                                    )}
                                  >
                                    {q.answer && (
                                      <RichTextBlock
                                        value={q.answer}
                                        className="select-none prose-p:text-[#1C1F22]/80 prose-p:text-[2rem] [&_ul]:pl-0 [&_ul]:pt-0 [&_ul]:!text-[#1C1F22]/80 [&_ul]:-mt-[1rem]"
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {!isOpen &&
                                !isNextOpen &&
                                qIndex <
                                  (faqItem.questions?.length ?? 0) - 1 && (
                                  <div
                                    className={`w-full border-t border-dashed `}
                                  />
                                )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>

                    {faqItemIndex < activeFaqGroup.faqItems.length - 1 && (
                      <div className="w-full bg-black/20 h-[.125rem] my-[6rem] lg:my-[12rem]" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <LastCTASection
        className="w-full"
        chip={data?.ctaChip}
        text={data?.ctaTitle}
        cta={data?.ctaCtA}
      />

      <Footer
        className="w-full"
        data={data?.settings?.footer}
        slug={data?.settings?.slug?.current}
      />
    </main>
  );
}
